import { useEffect, useState } from 'react';
import './news-view.styles.scss';
import { Link } from 'react-router-dom';
import { listenToNews } from '@/utils/getDataFromDb';
import TeamBadge from '@/components/SHARED/team-badge/team-badge.component';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';

const DAYS_PER_PAGE = 7;

// League news (leagues/{id}/news, written by league/news.js on each game
// day): headlines for the league, each conference, and (with highlightId)
// the manager's own team, grouped by day.
// teams: summary/teams map. myConference: the conference shown first.
const NewsView = ({ league, teams, teamLink, gameLink, playerLink, highlightId, myConference }) => {

    const [ days, setDays ] = useState(undefined);
    const [ count, setCount ] = useState(DAYS_PER_PAGE);
    const [ tab, setTab ] = useState('league');
    const conferences = Object.values(league.conferences ?? {}).sort((a, b) => a.name.localeCompare(b.name));
    const [ confId, setConfId ] = useState(myConference ?? conferences[0]?.id);

    useEffect(() => {
        let unsub;
        listenToNews({ leagueId: league.id, limit: count, callback: setDays, handleListener: (u) => { unsub = u; } });
        return () => { if (unsub) unsub(); };
    }, [league.id, count])

    if (days === undefined) return <div className='center-col'><ThreeDotsLoader /></div>;

    // The items a tab shows from one day.
    const itemsOf = (day) => {
        if (tab === 'league') return day.league ?? [];
        if (tab === 'conference') return day.conferences?.[confId] ?? [];
        const seen = new Set();
        return [...(day.league ?? []), ...Object.values(day.conferences ?? {}).flat()]
            .filter(i => i.teamIds?.includes(highlightId) && !seen.has(i.id) && seen.add(i.id));
    };
    const shown = days.map(d => ({ day: d, items: itemsOf(d) })).filter(d => d.items.length);
    const tabs = [
        { key: 'league', label: 'League' },
        { key: 'conference', label: 'Conference' },
        ...(highlightId ? [{ key: 'team', label: 'My team' }] : []),
    ];

    return (
        <div className='news-view'>
            <div className='g-card'>
                <h3>League news</h3>
                <div className='chips' role='tablist' aria-label='News'>
                    {tabs.map(t => (
                        <button key={t.key} role='tab' aria-selected={t.key === tab} className={`chip ${t.key === tab ? 'active' : ''}`} onClick={() => setTab(t.key)}>{t.label}</button>
                    ))}
                </div>
                {tab === 'conference' && conferences.length > 1 &&
                    <label className='conference-pick'>
                        <span className='meta'>Conference</span>
                        <select value={confId} onChange={(e) => setConfId(e.target.value)}>
                            {conferences.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </label>
                }
            </div>

            {shown.length === 0 &&
                <div className='g-card'>
                    <p className='meta'>
                        {days.length === 0 ? 'No news yet. Headlines come out after each game day.' :
                            tab === 'team' ? 'Your team hasn’t made the news lately.' : 'Nothing here lately.'}
                    </p>
                </div>
            }

            {shown.map(({ day, items }) => (
                <section key={day.key} className='g-card news-day'>
                    <h4>{dayHeading(day, league)}</h4>
                    <ul className='news-list'>
                        {items.map(item => (
                            <NewsItem key={item.id} item={item} teams={teams} teamLink={teamLink} gameLink={gameLink} playerLink={playerLink} highlightId={highlightId} />
                        ))}
                    </ul>
                </section>
            ))}

            {days.length === count &&
                <button className='g-button more' onClick={() => setCount(c => c + DAYS_PER_PAGE)}>Show more</button>
            }
        </div>
    )
}

// One headline with its details and links.
export const NewsItem = ({ item, teams, teamLink, gameLink, playerLink, highlightId }) => {
    const badges = (item.teamIds ?? []).slice(0, 2).map(id => teams?.[id]).filter(Boolean);
    const mine = highlightId && item.teamIds?.includes(highlightId);
    return (
        <li className={`news-item ${mine ? 'highlight' : ''}`}>
            <div className='news-badges'>
                {badges.map(t => (
                    <Link key={t.id} to={teamLink(t.id)} aria-label={t.name}><TeamBadge team={t} size='sm' /></Link>
                ))}
            </div>
            <div className='news-body'>
                <p className='news-headline'>{item.headline}</p>
                {item.details?.map((d, i) => <p key={i} className='news-detail'>{d}</p>)}
                {(item.gameId || item.playerId) &&
                    <p className='news-links'>
                        {item.gameId && <Link to={gameLink(item.gameId)}>Box score</Link>}
                        {item.playerId && playerLink && <Link to={playerLink(item.playerTeamId, item.playerId)}>Player</Link>}
                    </p>
                }
            </div>
        </li>
    );
}

// "Day 31", "Day 36 · Conference tournaments", "Last season · Day 40".
function dayHeading(day, league) {
    const year = league.season?.year ?? 1;
    const when = day.year === year ? `Day ${day.day}` :
        day.year === year - 1 ? `Last season · Day ${day.day}` : `Season ${day.year} · Day ${day.day}`;
    return day.label ? `${when} · ${day.label}` : when;
}

export default NewsView
